import ecc from '@bitcoinerlab/secp256k1';
import {Common} from '@ethereumjs/common';
import {LegacyTransaction} from '@ethereumjs/tx';
import {CurrencyCode, Uuid} from '@raison/types';
import {decode as decodeBase64} from '@stablelib/base64';
import {decode as decodeUtf8} from '@stablelib/utf8';
import {AxiosError} from 'axios';
import BIP32Factory from 'bip32';
import * as bitcoin from 'bitcoinjs-lib';
import bs58check from 'bs58check';
import {Buffer} from 'buffer';
import ECPairFactory from 'ecpair';
import nacl from 'tweetnacl';

function configChain(chainId: string) {
    return Common.custom({chainId: Number(chainId)});
}

function getIndex(signArr: string[]) {
    if (signArr?.length) return Number(signArr[0]);
    return 0;
}

export interface TransactionHolder {
    uuid: Uuid;
    key: string;
    sign: number[];
    tx: string;
    two_fa_box_uuid: Uuid;
    tenant_uuid: Uuid;
    expires_in: number;
}

export interface TransactionSignature {
    uuid: Uuid;
    tx: string;
}

/**
 * @description Signs cryptotransaction and returns object with fields uuid and tx.
 * <br/>&nbsp;&nbsp;&nbsp;&nbsp; uuid contains operation id
 * <br/>&nbsp;&nbsp;&nbsp;&nbsp; tx contains signed cryptotransaction
 * @param key - key from /v1/2fa/get
 * @param networkName - network name of blockchain
 *<br/>&nbsp;
 * @param pass - hashed pass (user password + user phone)
 *<br/>&nbsp;
 * @param answerBody - response from PUT query /api/v1/wallets/{id}/{operationType}
 *<br/>&nbsp;
 * @param currency - currency name of the wallet
 *<br/>&nbsp;
 * @return Object {uuid: string; tx: string}
 */
export function cryptoSign(
    key: string,
    networkName: string,
    pass: string,
    answerBody: TransactionHolder,
    currency: CurrencyCode
): TransactionSignature {
    let network = bitcoin.networks.bitcoin;
    if (networkName === 'testnet3') {
        network = bitcoin.networks.testnet;
    }

    try {
        if (/ETH|CELO/.test(currency)) {
            return processEth(answerBody, pass, key, network);
        } else {
            return process(answerBody, pass, key, network);
        }
    } catch (e) {
        console.log('crypto sign process error', e);
        if (e instanceof AxiosError) throw new Error(e.message);
        throw e;
    }
}

function process(rawBody: TransactionHolder, passHash: string, codeHash: string, network: bitcoin.networks.Network) {
    const answer = rawBody;
    const cryptKeyBase64 = answer.key;
    const ecPair = ECPairFactory(ecc);
    const bip32ExtKey = decryptBip32Key(cryptKeyBase64, codeHash, passHash, network);
    const txBuild = JSON.parse(Buffer.from(answer.tx, 'base64').toString('ascii'));
    const tx = new bitcoin.Psbt({network: network});
    // @ts-ignore
    tx.__CACHE.__UNSAFE_SIGN_NONSEGWIT = true;
    for (const inTx of txBuild.in) {
        tx.addInput({
            hash: inTx[0],
            index: inTx[1],
            witnessUtxo: {script: Buffer.from(inTx[2], 'hex'), value: +inTx[3].replace('.', '').replace(/^0+/, '')},
        });
    }
    for (const outTx of txBuild.out) {
        tx.addOutput({address: outTx[0], value: outTx[1]});
    }
    let i = 0;
    for (const _dummy in answer.sign) {
        const derive = answer.sign[i];
        const addrNode = bip32ExtKey.derive(derive);
        if (!addrNode.privateKey) {
            throw new Error(`Cannot derive addrNode with privateKey from ${derive}`);
        }
        tx.signInput(i++, ecPair.fromPrivateKey(Buffer.from(addrNode.privateKey), {network: network}));
    }
    tx.finalizeAllInputs();
    const tx_hex = tx.extractTransaction().toHex();
    return {
        uuid: answer.uuid as Uuid,
        tx: tx_hex,
    };
}

// CELO or ETH
function processEth(rawBody: TransactionHolder, passHash: string, codeHash: string, network: bitcoin.networks.Network) {
    const answer = rawBody;
    const cryptKeyBase64 = answer.key;
    const bip32ExtKey = decryptBip32Key(cryptKeyBase64, codeHash, passHash, network);
    const index = getIndex(answer.sign.map(String));
    const a0 = bip32ExtKey.derive(index);
    const wif = a0.toWIF();
    let privKey = Buffer.from(bs58check.decode(wif)).toString('hex');
    privKey = privKey.substring(2, privKey.length - 2);
    const privateKey = Buffer.from(privKey, 'hex');
    const {chainId, ...txBuild} = JSON.parse(Buffer.from(answer.tx, 'base64').toString('ascii'));
    txBuild['gasLimit'] = txBuild['gas'];
    const configuredChain = configChain(chainId);
    const tx = new LegacyTransaction(txBuild, {common: configuredChain});
    const signedTx = tx.sign(privateKey);
    return {
        uuid: answer.uuid as Uuid,
        tx: Buffer.from(signedTx.serialize()).toString('hex'),
    };
}

function decrypt(binaryMessage: Uint8Array, hexKey: string) {
    const key = Buffer.from(hexKey, 'hex');
    const nonce = binaryMessage.slice(0, 24);
    const ciphertext = binaryMessage.slice(24);
    return nacl.secretbox.open(ciphertext, nonce, key);
}

function decryptBip32Key(
    cryptKeyBase64: string,
    codeHash: string,
    passHash: string,
    network: bitcoin.networks.Network
) {
    const binaryCryptKey = decodeBase64(cryptKeyBase64);
    const firstDecrypt = decrypt(binaryCryptKey, codeHash);
    if (!firstDecrypt) {
        throw 'Error sms';
    }
    const bip32ExtKeyBytesArr = decrypt(firstDecrypt, passHash);
    if (!bip32ExtKeyBytesArr) {
        throw 'Error passwd';
    }
    const bip32ExtKey = decodeUtf8(bip32ExtKeyBytesArr);
    if (bip32ExtKey.indexOf('tprv') !== 0 && bip32ExtKey.indexOf('xprv') !== 0) {
        throw 'invalid decoded key';
    }
    return BIP32Factory(ecc).fromBase58(bip32ExtKey, network);
}
